import { getCachedGeocode, setCachedGeocode } from './geocodeCache';
import { buildClientGeocodeQueryVariants } from './clientAddress';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_USER_AGENT = 'AllegroIntegrator-CRM/1.0 (customer-map)';
const REQUEST_DELAY_MS = 1200;
const RETRY_DELAY_MS = 2500;
const PROGRESS_UPDATE_INTERVAL = 25;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransientGeocodeError = (error) =>
  /HTTP (429|502|503|504)/.test(error?.message || '');

const parseStructuredVariant = (variant) => {
  if (!variant.startsWith('street:')) return null;
  const parts = variant.split('|');
  return {
    street: parts[0]?.replace(/^street:/, '') || '',
    city: parts[1]?.replace(/^city:/, '') || '',
    postalCode: parts[2]?.replace(/^postal:/, '') || '',
  };
};

const geocodeWithNominatimStructured = async ({ street, city, postalCode }) => {
  const params = new URLSearchParams({
    street,
    city,
    country: 'Polska',
    format: 'json',
    limit: '1',
  });

  if (postalCode) {
    params.set('postalcode', postalCode);
  }

  const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'pl',
      'User-Agent': NOMINATIM_USER_AGENT,
    },
  });

  if (!response.ok) {
    throw new Error(`Geokodowanie nie powiodło się (HTTP ${response.status})`);
  }

  const results = await response.json();
  const first = results?.[0];
  if (!first?.lat || !first?.lon) return null;

  return {
    lat: Number(first.lat),
    lng: Number(first.lon),
    formattedAddress: first.display_name,
  };
};

const geocodeWithNominatim = async (query) => {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: '1',
    countrycodes: 'pl',
    addressdetails: '0',
  });

  const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'pl',
      'User-Agent': NOMINATIM_USER_AGENT,
    },
  });

  if (!response.ok) {
    throw new Error(`Geokodowanie nie powiodło się (HTTP ${response.status})`);
  }

  const results = await response.json();
  const first = results?.[0];
  if (!first?.lat || !first?.lon) return null;

  return {
    lat: Number(first.lat),
    lng: Number(first.lon),
    formattedAddress: first.display_name,
  };
};

const lookupVariant = async (variant) => {
  const structured = parseStructuredVariant(variant);
  if (structured?.street && structured?.city) {
    return geocodeWithNominatimStructured(structured);
  }
  return geocodeWithNominatim(variant);
};

export const geocodeAddress = async (primaryQuery, queryVariants = [primaryQuery]) => {
  if (!primaryQuery) return null;

  const cached = getCachedGeocode(primaryQuery);
  if (cached) return cached;

  const variants = [...new Set((queryVariants.length ? queryVariants : [primaryQuery]).filter(Boolean))];
  let lastTransientError = null;

  for (let index = 0; index < variants.length; index += 1) {
    const variant = variants[index];

    try {
      let result = await lookupVariant(variant);
      if (!result) {
        await sleep(RETRY_DELAY_MS);
        result = await lookupVariant(variant);
      }

      if (result) {
        setCachedGeocode(primaryQuery, {
          ...result,
          approximate: index > 0,
          matchedQuery: variant.startsWith('street:') ? 'structured' : variant,
        });
        await sleep(REQUEST_DELAY_MS);
        return getCachedGeocode(primaryQuery);
      }
    } catch (error) {
      if (isTransientGeocodeError(error)) {
        lastTransientError = error;
        await sleep(RETRY_DELAY_MS);
        continue;
      }
      throw error;
    }

    if (index < variants.length - 1) {
      await sleep(REQUEST_DELAY_MS);
    }
  }

  if (lastTransientError) {
    throw lastTransientError;
  }

  await sleep(REQUEST_DELAY_MS);
  return null;
};

const toLocation = (client, coords) => ({
  id: client.id,
  companyName: client.companyName,
  city: client.city,
  status: client.status,
  query: client.geocodeQuery,
  approximate: Boolean(coords.approximate),
  ...coords,
});

export const resolveClientGeocodeState = (clients) => {
  const locations = [];
  const pending = [];
  const failedIds = [];

  clients.forEach((client) => {
    const query = client.geocodeQuery;

    if (!query) {
      failedIds.push(client.id);
      return;
    }

    const cached = getCachedGeocode(query);
    if (cached) {
      locations.push(toLocation(client, cached));
      return;
    }

    pending.push(client);
  });

  return { locations, pending, failedIds };
};

export const geocodeClientsBatch = async (clients, { onProgress } = {}) => {
  const { locations, pending, failedIds } = resolveClientGeocodeState(clients);
  const failed = [...failedIds];
  const retryQueue = [];

  const cachedCount = locations.length + failedIds.length;
  if (cachedCount) {
    onProgress?.(cachedCount, clients.length);
  }

  for (let index = 0; index < pending.length; index += 1) {
    const client = pending[index];
    const variants = buildClientGeocodeQueryVariants(client);

    try {
      const coords = await geocodeAddress(client.geocodeQuery, variants);
      if (coords) {
        locations.push(toLocation(client, coords));
      } else {
        failed.push(client.id);
      }
    } catch (error) {
      if (isTransientGeocodeError(error)) {
        retryQueue.push(client);
      } else {
        failed.push(client.id);
      }
    }

    const done = cachedCount + index + 1;
    if (done === clients.length || (index + 1) % PROGRESS_UPDATE_INTERVAL === 0) {
      onProgress?.(Math.min(done, clients.length), clients.length);
    }
  }

  for (let index = 0; index < retryQueue.length; index += 1) {
    const client = retryQueue[index];
    const variants = buildClientGeocodeQueryVariants(client);

    try {
      await sleep(RETRY_DELAY_MS);
      const coords = await geocodeAddress(client.geocodeQuery, variants);
      if (coords) {
        locations.push(toLocation(client, coords));
        const failedIndex = failed.indexOf(client.id);
        if (failedIndex >= 0) failed.splice(failedIndex, 1);
      } else if (!failed.includes(client.id)) {
        failed.push(client.id);
      }
    } catch {
      if (!failed.includes(client.id)) {
        failed.push(client.id);
      }
    }
  }

  onProgress?.(clients.length, clients.length);

  return { locations, failedIds: failed };
};

export const resolveCachedClientLocations = resolveClientGeocodeState;
